import styled, { css } from 'styled-components';
import { AllieButton } from '../Buttons/Button';
import {
  AllieSectionGap,
  AllieSectionType,
  AllieSectionVariant,
  NumberCircleProps,
} from './Sections.types';

const styleColors = {
  primary: {
    selected: {
      background: '#2C1C5F',
      border: 'var(--allie-color-primary-80)',
      color: 'var(--allie-color-primary-30)',
    },
    unselected: {
      background: 'var(--allie-color-primary-5)',
      border: '#E9D7FE',
      color: 'var(--allie-color-primary-70)',
    },
  },
  error: {
    selected: {
      background: '#55160C',
      border: 'var(--allie-color-error-80)',
      color: 'var(--allie-color-error-30)',
    },
    unselected: {
      background: 'var(--allie-color-error-5)',
      border: 'var(--allie-color-error-60)',
      color: 'var(--allie-color-error-70)',
    },
  },
};

const getStyles = (
  variant: 'primary' | 'error',
  selected: boolean,
  alwaysSameColor?: 'selected' | 'unselected'
) => {
  if (alwaysSameColor) {
    return styleColors[variant][alwaysSameColor];
  }
  return styleColors[variant][selected ? 'selected' : 'unselected'];
};

const variants = {
  primary: {
    button: css<{ isSelected: boolean }>`
      --background: ${(props) =>
        props.isSelected ? 'var(--allie-color-primary-90)' : 'transparent'} !important;
      --color: ${(props) =>
        props.isSelected ? 'var(--allie-color-white)' : 'var(--allie-color-neutral-50)'};

      &:hover {
        --background: ${(props) =>
          props.isSelected ? 'var(--allie-color-primary-90)' : 'var(--allie-color-neutral-20)'};
      }
    `,
  },
  'primary-soft': {
    button: css<{ isSelected: boolean }>`
      --background: ${(props) =>
        props.isSelected ? 'var(--allie-color-primary-20)' : 'transparent'} !important;
      --color: ${(props) =>
        props.isSelected ? 'var(--allie-color-primary-70)' : 'var(--allie-color-neutral-50)'};
      --border-style: none;
      --border-width: 0;
      --border-color: transparent;
      --padding-top: 6px;
      --padding-bottom: 6px;

      &:hover {
        --background: ${(props) =>
          props.isSelected ? 'var(--allie-color-primary-20)' : 'var(--allie-color-neutral-20)'};
      }
    `,
  },
  secondary: {
    button: css<{ isSelected: boolean }>`
      --background: transparent;
      border-bottom: 2px solid
        ${(props) => (props.isSelected ? 'var(--allie-color-primary-60)' : 'transparent')};
      border-radius: 0;
      --border-style: none;

      &:hover {
        background-color: var(--allie-color-neutral-10);
      }
    `,
  },
  light: {
    button: css<{ isSelected: boolean }>`
      --background: ${(props) =>
        props.isSelected ? 'var(--allie-color-neutral-20)' : 'transparent'};
      border-radius: 0;
      --border-style: none;

      &:hover {
        --background: ${(props) =>
          props.isSelected ? 'var(--allie-color-neutral-20)' : 'var(--allie-color-neutral-10)'};
      }
    `,
  },
};

const getSectionVariantStyles = (variant: AllieSectionVariant) =>
  variants[variant] || variants.primary;

export const NumberCircle = styled.div<NumberCircleProps>`
  width: ${(props) => Math.max(16, 16 + props.digits * 4)}px;
  height: 19px;
  border-radius: 12px;
  background-color: ${(props) =>
    getStyles(props.variant, props.selected, props.alwaysSameColor).background};
  border: 1px solid
    ${(props) => getStyles(props.variant, props.selected, props.alwaysSameColor).border};
  color: ${(props) => getStyles(props.variant, props.selected, props.alwaysSameColor).color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 12px;
  flex-shrink: 0;
`;

export const ContentButton = styled.div`
  display: flex;
  align-items: center;
  gap: var(--allie-spacing-md);
  width: 100%;
`;

export const SectionButtonContainer = styled.div<{
  variant: AllieSectionVariant;
  gap: AllieSectionGap;
  direction?: AllieSectionType;
}>`
  display: flex;
  align-items: center;
  gap: ${(props) => `var(--allie-spacing-${props.gap})`};
  flex-direction: ${({ direction }) => (direction === 'column' ? 'column' : 'row')};
`;

export const SectionButton = styled(AllieButton)<{
  isSelected: boolean;
  variant: AllieSectionVariant;
  isMobile?: boolean;
  direction?: AllieSectionType;
}>`
  border: none;
  cursor: pointer;
  border-radius: 8px;
  --box-shadow: none;
  box-shadow: none;
  transition: all 0.3s ease;
  margin: 0;
  & span {
    display: flex;
    gap: 8px;
    align-items: center;
    ion-icon {
      width: 20px;
      height: 20px;
    }
    & svg {
      width: 20px;
    }
  }
  ${(props) => getSectionVariantStyles(props.variant).button}

  ${({ isMobile }) =>
    isMobile
      ? css`
          border: 1px solid var(--allie-color-neutral-50);
          border-radius: var(--allie-spacing-lg);
        `
      : ''}

  ${({ direction }) =>
    direction === 'column' &&
    css`
      width: 100%;
      --padding-start: var(--allie-spacing-lg);
      letter-spacing: normal;

      .allie-text-button-xs {
        width: 100%;
      }
    `}

  ${({ isMobile, direction }) =>
    isMobile && direction === 'column'
      ? css`
          border: none;
          border-radius: 0;
          border-bottom: 1px solid var(--allie-color-neutral-20);
          padding: var(--allie-spacing-md) 0;
        `
      : ''}
`;
